'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { ReactNode, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { CurrencyDisplay } from '@flipster/features/src/misc/display';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { FormattedMessage } from '@flipster/i18n';
import { walletsBunja } from '@flipster/r230522/original/wallet';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import BoltIcon from '@flipster/react/icon/generated/BoltIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { useLocalStorage } from '@flipster/react/misc/state/use-local-storage';
import { createStepComponent } from '@flipster/react/misc/step/Steps';
import { useSteps } from '@flipster/react/misc/step/use-steps';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { StakeEvent } from '@flipster/utils/segment/events/stake';

import { useUserPoint } from '@web/hooks/usePoint';

import { useLaunchpoolById } from '../../_hooks/use-launchpool-by-id';
import { StakingFormSchema, stakingFormValidator } from '../validation';
import HedgingNoticeForm from './HedgingNoticeForm';
import HedgingForm from './hedging-form';
import { HedgingFormContext } from './hedging-form/state';
import StakingCompletedForm from './staking-complete-form';
import StakingForm from './staking-form';

export type Steps = {
  staking: undefined;
  stakingComplete: undefined;
  hedgingNotice: undefined;
  hedging: undefined;
};

const Steps = createStepComponent();

interface Props {
  launchpoolId: number;
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
  step?: keyof Steps;
}

const StakingModal = ({ launchpoolId, isOpen, onClose, onExit, step = 'staking' }: Props) => {
  const { userAtom } = useBunja(authBunja);
  const user = useAtomValue(userAtom);
  const { walletsAtom } = useBunja(walletsBunja);
  const wallet = useAtomValue(walletsAtom);

  const methods = useForm<StakingFormSchema>({
    mode: 'onChange',
    resolver: yupResolver(stakingFormValidator),
  });
  const { current, push } = useSteps<Steps>({
    initialValue: { step, context: undefined },
  });
  const currentLaunchpool = useLaunchpoolById(launchpoolId);
  const userPointQuery = useUserPoint();
  const [isSkipHedgingNotice, setIsSkipHedgingNotice] = useLocalStorage({
    key: `flipster.configs.v1.${user?.userId}.skipHedgingNoticeConfirm`,
    defaultValue: false,
  });

  const stakingCurrency = currentLaunchpool?.stakingCurrency ?? 'unknown';
  const isSparkLaunchpool = currentLaunchpool?.isSparkLaunchpool;
  const availableAmount =
    (isSparkLaunchpool ? userPointQuery.data?.points : wallet?.[stakingCurrency]?.walletBalanceExclLocked) || '0';
  const { reset, getValues } = methods;

  const onCloseModal = () => {
    if (current.step === 'hedgingNotice' && user?.userId) {
      if (!isSkipHedgingNotice) {
        setIsSkipHedgingNotice(true);
      }
    }
    onClose();
    reset();
  };

  const onClickHedgeTooltip = () => {
    push({ step: 'hedgingNotice', context: undefined });
    segment.track(StakeEvent.StakeHedgeTooltipView, { currency: stakingCurrency });
  };

  const modalHeader: Record<keyof Steps, ReactNode | null> = {
    staking: (
      <div>
        {isSparkLaunchpool ? (
          <>
            <BoltIcon size={32} />
            Enter Flipster Sparks to burn
          </>
        ) : (
          <CurrencyDisplay
            currency={stakingCurrency}
            icon={<CurrencyDisplay.Icon size={24} space={12} />}
            mainLabel={
              <span className={hstack({ gap: 4 })}>
                Stake <CurrencyDisplay.Label value='shortName' />
              </span>
            }
          />
        )}
      </div>
    ),
    stakingComplete: null,
    hedgingNotice: (
      <p>
        <FormattedMessage id='Autogen_common.r251126.s_Hedge_to_protect_staked_assets_against_market_drops' />
      </p>
    ),
    hedging: (
      <p className={css({ display: 'inline' })}>
        <span>
          <FormattedMessage id='Autogen_common.r251126.s_Hedge_to_protect_staked_assets_against_market_drops' />
        </span>
        <button
          type='button'
          onClick={onClickHedgeTooltip}
          className={css({ color: 'content.default.level-1', ml: 4 })}
        >
          <InformationCircleIcon size={16} />
        </button>
      </p>
    ),
  };

  const hedgingFormContextValue = useMemo(
    () => ({
      stakingCurrency,
      stakedAmount: currentLaunchpool?.stakedAmount ?? '0',
    }),
    [stakingCurrency, currentLaunchpool],
  );

  useEffect(() => {
    if (currentLaunchpool) {
      const currentAmount = getValues('amount');
      reset({
        amount: currentAmount,
        minAmount: currentLaunchpool.minStakableAmount,
        maxAmount: currentLaunchpool.maxStakableAmount,
        currency: currentLaunchpool.stakingCurrency,
        availableAmount,
        isSparkLaunchpool,
      });
    }
  }, [currentLaunchpool, availableAmount, isSparkLaunchpool, reset, getValues]);

  if (!(launchpoolId !== null && currentLaunchpool)) return null;

  return (
    <Modal isOpen={isOpen} size='small' onExit={onExit}>
      <Modal.Close onClose={onCloseModal} />
      <Modal.Header css={{ textStyle: 'Title/M', color: 'content.default.level-5' }}>
        {modalHeader[current.step]}
      </Modal.Header>
      <FormProvider {...methods}>
        <Steps model={current}>
          <Steps.Step step='staking'>
            {() => <StakingForm pool={currentLaunchpool} onClose={onCloseModal} setModalStep={push} />}
          </Steps.Step>
          <Steps.Step step='stakingComplete'>
            {() => <StakingCompletedForm pool={currentLaunchpool} onClose={onCloseModal} setModalStep={push} />}
          </Steps.Step>
          <Steps.Step step='hedgingNotice'>
            {() => <HedgingNoticeForm stakingCurrency={stakingCurrency} onClose={onCloseModal} setModalStep={push} />}
          </Steps.Step>
          <Steps.Step step='hedging'>
            {() => (
              <HedgingFormContext value={hedgingFormContextValue}>
                <HedgingForm onClose={onCloseModal} />
              </HedgingFormContext>
            )}
          </Steps.Step>
        </Steps>
      </FormProvider>
    </Modal>
  );
};

export default StakingModal;
